import { useEffect, useState } from "react";
import { PROJECTS as LOCAL_PROJECTS } from "../constants/projects";
import { PRODUCTS as LOCAL_PRODUCTS } from "../constants/products";
import type { Project } from "../constants/projects";
import type { Product } from "../constants/products";
import { SITE, resolveLinks } from "../constants/domains";

const BASE_URL = `${SITE.static}/data/lab`;

interface RemotePayload<T> {
  generatedAt: string;
  source: string;
  items: T[];
}

export function useRemoteData<T extends { slug: string }>(
  endpoint: "projects" | "products",
  fallback: T[]
) {
  const [data, setData] = useState<T[]>(fallback);
  const [loading, setLoading] = useState(true);
  const [usedFallback, setUsedFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;

    fetch(`${BASE_URL}/${endpoint}.json`)
      .then((res) => {
        if (!res.ok) throw new Error(`${res.status}`);
        return res.json();
      })
      .then((payload: RemotePayload<T>) => {
        if (cancelled) return;
        if (Array.isArray(payload.items) && payload.items.length > 0) {
          setData(resolveLinks(payload.items));
        } else {
          setUsedFallback(true);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setUsedFallback(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [endpoint]);

  return { data, loading, usedFallback };
}

export function useRemoteProjects() {
  return useRemoteData<Project>("projects", LOCAL_PROJECTS);
}

export function useRemoteProducts() {
  return useRemoteData<Product>("products", LOCAL_PRODUCTS);
}
